import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaCalendarAlt, FaPrint, FaRedo, FaSyncAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import Loader from "../loader/Loader";
import "./DispatchPlan.css";

const DispatchPlans = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [search, setSearch] = useState("");
    const [month, setMonth] = useState(
        new Date().toISOString().slice(0, 7)
    );

    useEffect(() => {
        fetchDispatchPlan();
    }, []);

    const fetchDispatchPlan = async () => {
        const token = getAuthToken();

        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        try {
            setLoading(true);

            const response = await axios.get(
                `${config.API}/senmanager/getDispatchPlan`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.success) {
                setData(response.data?.data || []);
            } else {
                setData([]);
                toast.warning(
                    response.data?.message || "No dispatch plan found."
                );
            }
        } catch (error) {
            console.error("Dispatch Plan Error:", error);
            setData([]);

            toast.error(
                error.response?.data?.message ||
                    "Failed to load dispatch plan."
            );
        } finally {
            setLoading(false);
        }
    };

    const resetFilter = () => {
        setSearch("");
        setMonth(new Date().toISOString().slice(0, 7));
    };

    const formatQty = (qty) =>
        qty
            ? Number(qty).toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
              })
            : "-";

    /*
     * 12 months starting from selected month
     */
    const months = useMemo(() => {
        const result = [];
        const start = new Date(`${month}-01T00:00:00`);

        for (let i = 0; i < 12; i++) {
            const date = new Date(start);
            date.setMonth(start.getMonth() + i);

            const key = `${date.getFullYear()}-${String(
                date.getMonth() + 1
            ).padStart(2, "0")}`;

            result.push({
                key,
                label: date.toLocaleDateString("en-IN", {
                    month: "short",
                    year: "2-digit",
                }),
            });
        }

        return result;
    }, [month]);

    /*
     * Search
     */
    const filteredData = useMemo(() => {
        const value = search.toLowerCase().trim();

        if (!value) return data;

        return data.filter((item) =>
            [
                item.product_code,
                item.product_name,
                item.po_number,
                item.railway,
            ].some((field) =>
                String(field || "")
                    .toLowerCase()
                    .includes(value)
            )
        );
    }, [data, search]);

    /*
     * Product wise grouping
     * One product = one row
     */
    const products = useMemo(() => {
        const grouped = {};

        filteredData.forEach((item) => {
            const code = item.product_code || "N/A";

            if (!grouped[code]) {
                grouped[code] = {
                    product_code: code,
                    product_name: item.product_name || "-",
                    months: {},
                };
            }

            if (!item.delivery_month_name) return;

            const date = new Date(
                `1 ${item.delivery_month_name}`
            );

            if (isNaN(date.getTime())) return;

            const key = `${date.getFullYear()}-${String(
                date.getMonth() + 1
            ).padStart(2, "0")}`;

            grouped[code].months[key] =
                (grouped[code].months[key] || 0) +
                (Number(item.total_pending_qty) || 0);
        });

        return Object.values(grouped);
    }, [filteredData]);

    /*
     * Month totals
     */
    const totals = useMemo(() => {
        const result = {};

        months.forEach((m) => {
            result[m.key] = 0;
        });

        products.forEach((product) => {
            months.forEach((m) => {
                result[m.key] +=
                    product.months[m.key] || 0;
            });
        });

        return result;
    }, [products, months]);

    const selectedMonthName = new Date(
        `${month}-01T00:00:00`
    ).toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
    });

    return (
        <div className="container-fluid py-2">

            {/* FILTER */}
            <div className="card border-0 shadow-sm mb-2 print-hide">
                <div className="card-body py-2">

                    <div className="row g-2 align-items-center">

                        <div className="col-12 col-md">
                            <h6 className="fw-bold text-primary mb-0">
                                Dispatch Plan
                            </h6>
                            <small className="text-muted">
                                12 month dispatch plan overview
                            </small>
                        </div>

                        <div className="col-12 col-sm-5 col-md-3">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text">
                                    🔍
                                </span>

                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search product / PO..."
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="col-6 col-sm-3 col-md-2">
                            <div className="input-group input-group-sm">
                                <span className="input-group-text">
                                    <FaCalendarAlt />
                                </span>

                                <input
                                    type="month"
                                    className="form-control"
                                    value={month}
                                    onChange={(e) =>
                                        setMonth(e.target.value)
                                    }
                                />
                            </div>
                        </div>

                        <div className="col-6 col-sm-auto">
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={fetchDispatchPlan}
                                disabled={loading}
                            >
                                <FaSyncAlt
                                    className={
                                        loading
                                            ? "spin"
                                            : ""
                                    }
                                />{" "}
                                Refresh
                            </button>
                        </div>

                        <div className="col-6 col-sm-auto">
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={resetFilter}
                            >
                                <FaRedo /> Reset
                            </button>
                        </div>

                        <div className="col-6 col-sm-auto">
                            <button
                                className="btn btn-sm btn-dark"
                                onClick={() => window.print()}
                            >
                                <FaPrint /> Print
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* DOCUMENT */}
            <div className="card border-0 shadow-sm">

                {/* HEADER */}
                <div className="card-body py-3 border-bottom">

                    <div className="d-flex justify-content-between align-items-center">

                        <div>
                            <h5 className="fw-bold text-primary mb-1">
                                Dispatch Plan - {selectedMonthName}
                            </h5>

                            <small className="text-muted">
                                Dispatch plan overview
                            </small>
                        </div>

                        <span className="badge bg-primary-subtle text-primary">
                            12 Month View
                        </span>

                    </div>

                </div>

                {/* TABLE */}
                {loading ? (
                    <div className="py-5 text-center">
                        <Loader />
                    </div>
                ) : (
                    <div className="table-responsive">

                        <table className="table table-hover align-middle mb-0 dispatch-table">

                            <thead>
                                <tr>
                                    <th className="code-width">
                                        Item Code
                                    </th>

                                    <th className="product-width">
                                        Product
                                    </th>

                                    {months.map((m) => (
                                        <th
                                            key={m.key}
                                            className="text-center"
                                        >
                                            {m.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>

                                {products.length ? (
                                    products.map((product) => (
                                        <tr
                                            key={
                                                product.product_code
                                            }
                                        >

                                            <td>
                                                <span className="badge text-bg-light border text-dark">
                                                    {
                                                        product.product_code
                                                    }
                                                </span>
                                            </td>

                                            <td className="fw-semibold">
                                                {
                                                    product.product_name
                                                }{" "}
                                                <span className="text-muted">
                                                    (
                                                    {
                                                        product.product_code
                                                    }
                                                    )
                                                </span>
                                            </td>

                                            {months.map((m) => {
                                                const qty =
                                                    product.months[
                                                        m.key
                                                    ] || 0;

                                                return (
                                                    <td
                                                        key={m.key}
                                                        className={`text-center ${
                                                            qty
                                                                ? "fw-bold"
                                                                : "text-muted"
                                                        }`}
                                                    >
                                                        {formatQty(
                                                            qty
                                                        )}
                                                    </td>
                                                );
                                            })}

                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={
                                                months.length + 2
                                            }
                                            className="text-center py-5 text-muted"
                                        >
                                            No dispatch plan
                                            found.
                                        </td>
                                    </tr>
                                )}

                            </tbody>

                            {/* TOTAL */}
                            {products.length > 0 && (
                                <tfoot>
                                    <tr className="fw-bold">

                                        <td colSpan="2">
                                            Total Dispatch
                                        </td>

                                        {months.map((m) => (
                                            <td
                                                key={m.key}
                                                className="text-center"
                                            >
                                                {formatQty(
                                                    totals[m.key]
                                                )}
                                            </td>
                                        ))}

                                    </tr>
                                </tfoot>
                            )}

                        </table>

                    </div>
                )}

                {/* SUMMARY */}
                {!loading && products.length > 0 && (
                    <div className="card-footer bg-light print-hide">

                        <div className="d-flex gap-4 small">

                            <span>
                                Products:{" "}
                                <strong>
                                    {products.length}
                                </strong>
                            </span>

                            <span>
                                Total:{" "}
                                <strong>
                                    {formatQty(
                                        Object.values(
                                            totals
                                        ).reduce(
                                            (a, b) =>
                                                a + b,
                                            0
                                        )
                                    )}
                                </strong>
                            </span>

                        </div>

                    </div>
                )}

            </div>
        </div>
    );
};

export default DispatchPlans;